import {
  Box,
  BoxProps,
  Flex,
  FlexAnchor,
  FlexAnchorProps,
  Icon,
  InputWrapper,
  smBumps,
  TextInput,
  Typo,
  xsBumps,
} from '@wrisk/ui-components'
import React, { FunctionComponent, useCallback, useState } from 'react'
import { useDebounce } from 'use-debounce'

import { Address, FindAddressItem, isAddress } from '../../../domain'
import {
  useApiErrorHandlingAsync,
  useApiErrorHandlingAsyncCallback,
} from '../../../hooks/auth'
import { usePrincipal } from '../../authentication'

export interface AddressSearchProps extends BoxProps {
  name?: string
  placeholder?: string
  onChange: (address: Address) => void

  delay?: number
  countries?: string[]
}

export const AddressSearch: FunctionComponent<AddressSearchProps> = ({
  name,
  delay = 250,
  placeholder,
  countries,
  onChange,
  ...props
}) => {
  const { apiClient } = usePrincipal()

  const [text, setText] = useState('')
  const [searchTerm] = useDebounce(text, delay)

  const [results, setResults] = useState<FindAddressItem[]>([])

  const { loading: termLoading } = useApiErrorHandlingAsync(
    async (text: string) =>
      setResults(text ? await apiClient.findAddresses(text, { countries }) : []),
    [searchTerm],
  )

  const { loading: itemLoading, execute: onItemClick } = useApiErrorHandlingAsyncCallback(
    async (item: FindAddressItem) => {
      isAddress(item)
        ? onChange(await apiClient.getAddress(item.id))
        : setResults(
            await apiClient.findAddresses(item.text, { countries, container: item.id }),
          )
    },
  )

  const loading = termLoading || itemLoading

  const onTextChange = useCallback((e) => setText(e.target.value), [])

  return (
    <Box width={1} {...props}>
      <InputWrapper width={1}>
        <Icon icon={loading ? 'loadingDark' : 'search'} size='small' mr={smBumps} />
        <TextInput
          disabled={itemLoading}
          flexGrow={1}
          variant='ghost'
          id={name}
          name={name}
          value={text}
          placeholder={placeholder}
          onChange={onTextChange}
        />
      </InputWrapper>

      {Boolean(results.length) && (
        <Box transition='default' variant='raised' mt={2} p={2}>
          {results.map((item) => (
            <AddressItemAnchor key={item.id} item={item} onClick={onItemClick} />
          ))}
        </Box>
      )}
    </Box>
  )
}

const AddressItemAnchor: FunctionComponent<
  Omit<FlexAnchorProps, 'onClick'> & {
    loading?: boolean
    item: FindAddressItem
    onClick: (item: FindAddressItem) => void
  }
  // eslint-disable-next-line
> = ({ item, loading, onClick, ref, ...props }) => {
  const onInternalClick = useCallback(() => onClick(item), [onClick, item])

  return (
    <FlexAnchor
      fontFamily='input'
      key={item.id}
      px={smBumps}
      py={xsBumps}
      variant='highlight'
      {...props}
      onClick={onInternalClick}
      disabled={loading}
    >
      <Flex width={1}>
        <Typo typoSize='sm' mr={2}>
          {item.text}
        </Typo>
        <Typo typoSize='sm' color='bodySecondary'>
          {item.description}
        </Typo>
      </Flex>
      {!isAddress(item) && (
        <Icon icon={loading ? 'loadingDark' : 'chevronRight'} size='tiny' ml={2} />
      )}
    </FlexAnchor>
  )
}
